import './Products.scss';

import cn from 'classnames';
import { omit, sortBy } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flipped, Flipper } from 'react-flip-toolkit';

import { ASC, DESC, RENDER_TYPE_LIST } from '@/common/constants';
import Product from '@/ebat/components/abstract/Product/Product';
import ProductTableProvider, {
  ProductsTableContext,
} from '@/ebat/components/blocks/Products/ProductsTableContext/ProductsTableContext';

import LocalSort from './LocalSort/LocalSort';
import { ProductsDefaultProps, ProductsPropTypes } from './Products.proptypes';

const Products = ({
  addToCartOptions,
  className,
  disabled,
  isLoading,
  localOptions,
  productType,
  products,
  sortables,
  source,
}) => {
  /**
   * Return sortables object with 3 items limit
   */
  const sortablesLimit = useMemo(
    () => omit(sortables, Object.keys(sortables).slice(3)),
    [sortables],
  );

  /**
   * Set init sort with first sortable or sku
   * @type {string}
   */
  const initSort = useMemo(
    () =>
      sortablesLimit[Object.keys(sortablesLimit)[0]]?.code
        ? `sortables.${
            sortablesLimit[Object.keys(sortablesLimit)[0]].code
          }.value`
        : 'sku',
    [sortablesLimit],
  );

  const [sortCode, setSortCode] = useState(initSort);
  const [sortDirection, setSortDirection] = useState(ASC);

  /**
   * Sort products data by code and direction
   * @returns {array}
   */
  const sortData = useMemo(() => {
    if (localOptions.length > 0 && sortCode && sortDirection) {
      const newSortData = sortBy(products, (data) => {
        const newSortCode = sortCode.split('.');
        const newData = newSortCode.reduce(
          (acc, item) => acc[item] || '',
          data,
        );

        if (!Number.isNaN(parseFloat(newData))) {
          return parseFloat(newData);
        }

        return newData;
      });

      if (sortDirection === DESC) {
        return newSortData.reverse();
      }

      return newSortData;
    }

    return products;
  }, [localOptions, products, sortCode, sortDirection]);

  /**
   * Return list of paths for sort animation
   * @type {array}
   */
  const getFlipKeys = useMemo(() => sortData.map((item) => item.path).join(), [
    sortData,
  ]);

  const classNames = useMemo(
    () => [`products-${productType}`, className].filter((val) => val).join(' '),
    [className, productType],
  );

  const [isSticky, setSticky] = useState(false);
  const localSortRef = useRef(null);
  const arrayRef = useRef(null);

  const prevScrollY = useRef(0);

  const handleScroll = () => {
    if (localSortRef.current && arrayRef.current) {
      const containerArrayRect = arrayRef.current.getBoundingClientRect();
      const isScrollingDown = window.scrollY > prevScrollY.current;
      const isTableTopAboveViewport = containerArrayRect.top < 89;
      const isTableTopInView =
        containerArrayRect.top >= 0 &&
        containerArrayRect.top <= window.innerHeight;
      const isTableBottomBelowViewport = containerArrayRect.bottom <= 90;

      let sticky = false;

      if (isScrollingDown) {
        sticky = isTableTopAboveViewport && !isTableBottomBelowViewport;
      } else {
        sticky =
          containerArrayRect.top <= window.innerHeight &&
          !isTableTopInView &&
          isTableBottomBelowViewport <= 0;
      }

      setSticky(sticky);
      prevScrollY.current = window.scrollY;
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const flipperRef = useRef(null);

  useEffect(() => {
    const flipperElement = document.querySelector('.flipper');

    const handleFlipperScroll = () => {
      if (flipperElement && localSortRef.current) {
        localSortRef.current.scrollLeft = flipperElement.scrollLeft || 0;
      }
    };

    if (flipperElement) {
      flipperElement.addEventListener('scroll', handleFlipperScroll);
    }

    return () => {
      if (flipperElement) {
        flipperElement.removeEventListener('scroll', handleFlipperScroll);
      }
    };
  }, []);

  return (
    <ProductTableProvider
      renderType={RENDER_TYPE_LIST}
      tableContainerRef={arrayRef}
    >
      <ProductsTableContext.Consumer>
        {({ tableRowSize }) => (
          <div className={cn(classNames, { sticky: isSticky })}>
            {localOptions.length > 0 && (
              <div
                ref={localSortRef}
                className={cn('sort-wrapper', `sort-wrapper-${productType}`, {
                  sticky: isSticky,
                })}
              >
                <LocalSort
                  disabled={disabled || isLoading}
                  isLoading={isLoading}
                  options={localOptions}
                  setSortCode={setSortCode}
                  setSortDirection={setSortDirection}
                  sortCode={sortCode}
                  sortDirection={sortDirection}
                  sortables={sortablesLimit}
                />
              </div>
            )}
            <Flipper
              className={cn('flipper', { sticky: isSticky })}
              flipKey={getFlipKeys}
              ref={flipperRef}
            >
              <ul
                className={cn('products', `products--${productType}`)}
                ref={arrayRef}
              >
                {sortData.map((product) => (
                  <Flipped
                    flipId={product.path}
                    key={`product-${product.path}-${product.slug}`}
                  >
                    <li
                      className="products__item"
                      data-qa-id="products-item"
                      data-qa-item-id={product.id}
                      data-qa-item-sku={product.sku}
                    >
                      <Product
                        disabled={disabled || isLoading}
                        isLoading={isLoading}
                        addToCartOptions={addToCartOptions}
                        product={product}
                        sortables={sortablesLimit}
                        type={productType}
                        isRelatedProduct
                        tableRowSize={tableRowSize}
                        source={source}
                      />
                    </li>
                  </Flipped>
                ))}
              </ul>
            </Flipper>
          </div>
        )}
      </ProductsTableContext.Consumer>
    </ProductTableProvider>
  );
};

Products.defaultProps = {
  ...ProductsDefaultProps,
};

Products.propTypes = {
  ...ProductsPropTypes,
};

export default Products;
